// Salva o gráfico como imagem sem biblioteca nova: clona o <svg> da tela, cola
// nele os estilos calculados (o tremor pinta por classe do Tailwind, e a classe
// não viaja junto do SVG serializado), desenha num canvas e baixa o PNG.

const PROPRIEDADES = [
    'fill',
    'stroke',
    'stroke-width',
    'stroke-dasharray',
    'opacity',
    'font-family',
    'font-size',
    'font-weight',
    'color',
]

const ESCALA = 2
const ALTURA_TITULO = 44
const ALTURA_LINHA_LEGENDA = 22
const LARGURA_PADRAO = 900
const ALTURA_PADRAO = 360
const MAX_LETRAS_LEGENDA = 40

// Mesmo saneamento do useExport, sem o "_" sobrando na ponta.
export function nomeArquivoGrafico(base) {
    const limpo = String(base || 'relatorio')
        .replace(/[^a-z0-9]/gi, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')

    return `${limpo || 'relatorio'}-grafico.png`.toLowerCase()
}

// Clona o SVG com os estilos que estavam valendo na tela, elemento por elemento.
export function clonarSvgComEstilos(svg) {
    if (!svg || typeof svg.cloneNode !== 'function') {
        return null
    }

    const clone = svg.cloneNode(true)
    const origens = [svg, ...svg.querySelectorAll('*')]
    const destinos = [clone, ...clone.querySelectorAll('*')]

    origens.forEach((origem, indice) => {
        const destino = destinos[indice]
        if (!destino || !destino.style) {
            return
        }

        const estilo = window.getComputedStyle(origem)
        if (!estilo) {
            return
        }

        PROPRIEDADES.forEach(propriedade => {
            const valor = estilo.getPropertyValue(propriedade)
            if (valor) {
                destino.style.setProperty(propriedade, valor)
            }
        })
    })

    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    return clone
}

// Tamanho que o gráfico tem na tela; sem isso o canvas não sabe o que desenhar.
export function medirSvg(svg) {
    const caixa = typeof svg?.getBoundingClientRect === 'function' ? svg.getBoundingClientRect() : null
    const largura = Math.round(caixa?.width || Number(svg?.getAttribute?.('width')) || LARGURA_PADRAO)
    const altura = Math.round(caixa?.height || Number(svg?.getAttribute?.('height')) || ALTURA_PADRAO)

    return { largura, altura }
}

export function svgParaTexto(clone, largura, altura) {
    clone.setAttribute('width', String(largura))
    clone.setAttribute('height', String(altura))
    if (!clone.getAttribute('viewBox')) {
        clone.setAttribute('viewBox', `0 0 ${largura} ${altura}`)
    }

    return new XMLSerializer().serializeToString(clone)
}

// A legenda do tremor é HTML fora do SVG: no PNG ela é desenhada à mão, uma
// linha por vez, quebrando quando os nomes das lojas não cabem na largura.
export function linhasDaLegenda(contexto, legenda, largura) {
    const linhas = []
    let atual = []
    let x = 16

    for (const item of legenda || []) {
        const nome = String(item?.nome || '')
        const texto = nome.length > MAX_LETRAS_LEGENDA ? `${nome.slice(0, MAX_LETRAS_LEGENDA - 1)}…` : nome
        const larguraTexto = contexto.measureText(texto).width + 34
        if (atual.length && x + larguraTexto > largura - 16) {
            linhas.push(atual)
            atual = []
            x = 16
        }
        atual.push({ texto, cor: item?.cor || '#3b82f6', x })
        x += larguraTexto
    }
    if (atual.length) {
        linhas.push(atual)
    }

    return linhas
}

function desenharLegenda(contexto, linhas, topo) {
    contexto.font = '13px Helvetica, Arial, sans-serif'
    linhas.forEach((linha, indice) => {
        const y = topo + indice * ALTURA_LINHA_LEGENDA
        linha.forEach(item => {
            contexto.fillStyle = item.cor
            contexto.fillRect(item.x, y, 12, 12)
            contexto.fillStyle = '#374151'
            contexto.fillText(item.texto, item.x + 18, y + 11)
        })
    })
}

function carregarImagem(url) {
    return new Promise((resolve, reject) => {
        const imagem = new Image()
        imagem.onload = () => resolve(imagem)
        imagem.onerror = () => reject(new Error('Não consegui desenhar o gráfico.'))
        imagem.src = url
    })
}

function paraBlob(canvas) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Não consegui gerar a imagem.'))), 'image/png')
    })
}

// Parte que depende do navegador: quem chama trata o erro e avisa na tela.
export async function baixarGraficoPng({ svg, titulo, nomeArquivo, legenda }) {
    const clone = clonarSvgComEstilos(svg)
    if (!clone) {
        throw new Error('Gráfico não encontrado na tela.')
    }

    const { largura, altura } = medirSvg(svg)
    const texto = svgParaTexto(clone, largura, altura)
    const imagem = await carregarImagem(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(texto)}`)

    const canvas = document.createElement('canvas')
    const contexto = canvas.getContext('2d')
    contexto.font = '13px Helvetica, Arial, sans-serif'
    const linhasLegenda = linhasDaLegenda(contexto, legenda, largura)
    const alturaLegenda = linhasLegenda.length * ALTURA_LINHA_LEGENDA
    const alturaTotal = altura + ALTURA_TITULO + alturaLegenda

    canvas.width = largura * ESCALA
    canvas.height = alturaTotal * ESCALA

    // Mudar o tamanho do canvas zera o contexto: a escala entra depois.
    contexto.scale(ESCALA, ESCALA)
    contexto.fillStyle = '#ffffff'
    contexto.fillRect(0, 0, largura, alturaTotal)

    if (titulo) {
        contexto.fillStyle = '#1f2937'
        contexto.font = 'bold 16px Helvetica, Arial, sans-serif'
        contexto.fillText(titulo, 16, 28)
    }

    if (linhasLegenda.length) {
        desenharLegenda(contexto, linhasLegenda, ALTURA_TITULO)
    }

    contexto.drawImage(imagem, 0, ALTURA_TITULO + alturaLegenda, largura, altura)

    const blob = await paraBlob(canvas)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = nomeArquivo || 'relatorio-grafico.png'
    link.style.visibility = 'hidden'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    // Firefox e Safari abortam o download se a URL morre no mesmo tick do clique.
    setTimeout(() => URL.revokeObjectURL(url), 0)
}
